import type { State, County, City } from './types';
import { US_STATES } from './data';
import { storage } from './storage';
import { normalizeZipCode } from './zip';

type FirestoreService = typeof import('./firestore-service');
let firestoreServicePromise: Promise<FirestoreService> | null = null;

function loadFirestoreService(): Promise<FirestoreService> {
  if (!firestoreServicePromise) firestoreServicePromise = import('./firestore-service');
  return firestoreServicePromise;
}

let geographicDataCache: State[] | null = null;
let isLoading = false;
const loadResolvers: Array<(data: State[]) => void> = [];
// Bump cache key to force clients to refresh with the latest Firestore data (Beta phase)
const CACHE_STORAGE_KEY = 'geographic-cache-v5';
const CACHE_TTL_HOURS = 6;

export interface GeographicCacheInfo {
  source: 'firestore' | 'seed' | 'fallback';
  fetchedAt: string;
}

interface CachedPayload {
  source: GeographicCacheInfo['source'];
  fetchedAt: string;
  states: State[];
}

function isCacheFresh(cache: CachedPayload | null): cache is CachedPayload {
  if (!cache) return false;
  // Empty fallback data must never suppress a later emulator/cloud retry.
  // A transient Firestore outage should not make every dashboard look empty
  // for the full six-hour cache window.
  if (cache.source === 'seed' || cache.source === 'fallback') return false;
  // Reject the old empty-emulator payload even when it was mistakenly labeled
  // as a successful Firestore read. Otherwise it suppresses live geography
  // for the entire cache window.
  if (
    !cache.states.some(
      (state) => state.counties.length > 0 || (state.countyCount || 0) > 0 || (state.cityCount || 0) > 0,
    )
  ) {
    return false;
  }
  const ageMs = Date.now() - new Date(cache.fetchedAt).getTime();
  return ageMs < CACHE_TTL_HOURS * 60 * 60 * 1000;
}

function mergeWithStateCatalog(states: State[]): State[] {
  const byCode = new Map(states.map((state) => [state.code.toUpperCase(), state]));
  return US_STATES.map((catalogState) => {
    const loaded = byCode.get(catalogState.code);
    return loaded || { ...catalogState, counties: [] };
  });
}

async function loadData(): Promise<State[]> {
  try {
    const cached = await storage.get<CachedPayload>(CACHE_STORAGE_KEY);
    if (isCacheFresh(cached)) {
      const merged = mergeWithStateCatalog(cached.states);
      geographicDataCache = merged;
      return merged;
    }
  } catch (error) {
    console.warn('[Geographic Data] Cache read failed, continuing to Firestore', error);
  }

  try {
    // Use shallow load for initial cache to improve performance
    const { loadStatesShallow } = await loadFirestoreService();
    const firestoreStates = await loadStatesShallow();

    if (firestoreStates && firestoreStates.length > 0) {
      const merged = mergeWithStateCatalog(firestoreStates);
      await storage.set<CachedPayload>(CACHE_STORAGE_KEY, {
        source: 'firestore',
        fetchedAt: new Date().toISOString(),
        states: merged,
      });
      return merged;
    }

    console.warn('[Geographic Data] No Firestore data returned, falling back to empty states list');
  } catch (error) {
    console.error('[Geographic Data] Error loading from Firestore:', error);
  }

  const emptyStates = US_STATES.map((state) => ({
    ...state,
    counties: [],
  }));

  // Keep the empty response as diagnostic state, but deliberately do not
  // treat it as a fresh cache hit on the next load.
  await storage.set<CachedPayload>(CACHE_STORAGE_KEY, {
    source: 'fallback',
    fetchedAt: new Date().toISOString(),
    states: emptyStates,
  });

  return emptyStates;
}

async function getGeographicData(): Promise<State[]> {
  if (geographicDataCache) {
    return geographicDataCache;
  }

  if (isLoading) {
    return new Promise((resolve) => {
      loadResolvers.push(resolve);
    });
  }

  isLoading = true;
  const data = await loadData();
  geographicDataCache = data;
  isLoading = false;

  loadResolvers.forEach((resolve) => resolve(data));
  loadResolvers.length = 0;

  return data;
}

const EMPTY_STATE_SNAPSHOT: State[] = US_STATES.map((state) => ({
  ...state,
  counties: [],
}));

export async function refreshGeographicData(): Promise<void> {
  geographicDataCache = null;
  await getGeographicData();
}

export async function getGeographicCacheInfo(): Promise<GeographicCacheInfo | null> {
  try {
    const cached = await storage.get<CachedPayload>(CACHE_STORAGE_KEY);
    if (!cached) return null;
    return { source: cached.source, fetchedAt: cached.fetchedAt };
  } catch (error) {
    console.warn('[Geographic Data] Unable to read cache info', error);
    return null;
  }
}

export function getAllStates(): State[] {
  return geographicDataCache ?? EMPTY_STATE_SNAPSHOT;
}

export async function getAllStatesAsync(): Promise<State[]> {
  return await getGeographicData();
}

export function getStateByCode(code: string): State | undefined {
  const source = geographicDataCache ?? EMPTY_STATE_SNAPSHOT;
  return source.find((s) => s.code.toLowerCase() === code.toLowerCase());
}

export async function getStateByCodeAsync(code: string): Promise<State | undefined> {
  const data = await getGeographicData();
  const state = data.find((s) => s.code.toLowerCase() === code.toLowerCase());

  // State pages need the county directory, not every city document in the
  // state. Load the selected county separately when its route opens.
  if (state && state.counties.length === 0) {
    console.log(`[Geographic Data] Loading county directory for ${state.code}...`);
    const { loadStateDirectory } = await loadFirestoreService();
    const directoryState = await loadStateDirectory(state.code);
    if (directoryState) {
      const index = data.findIndex((s) => s.code === state.code);
      if (index !== -1) {
        data[index] = directoryState;
      }
      return directoryState;
    }
  }

  return state;
}

export function getCountiesByState(stateCode: string): County[] {
  const state = getStateByCode(stateCode);
  return state?.counties || [];
}

export async function getCountiesByStateAsync(stateCode: string): Promise<County[]> {
  const state = await getStateByCodeAsync(stateCode);
  return state?.counties || [];
}

/**
 * County links exist in both canonical slug form ("yamhill") and legacy
 * route form ("yamhill-county"). Keep the stored Firestore slug canonical.
 */
export function normalizeCountyRouteValue(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+county$/i, '')
    .replace(/-county$/i, '');
}

export function getCounty(stateCode: string, countyName: string): County | undefined {
  const state = getStateByCode(stateCode);
  const requested = normalizeCountyRouteValue(countyName);
  return state?.counties.find(
    (c) =>
      normalizeCountyRouteValue(c.name) === requested || (c.slug && normalizeCountyRouteValue(c.slug) === requested),
  );
}

export async function getCountyAsync(stateCode: string, countyName: string): Promise<County | undefined> {
  try {
    const { loadCountyDirect } = await loadFirestoreService();
    const directCounty = await loadCountyDirect(stateCode, countyName);
    if (directCounty) {
      const data = await getGeographicData();
      const state = data.find((candidate) => candidate.code.toLowerCase() === stateCode.toLowerCase());
      if (state) {
        const requested = normalizeCountyRouteValue(countyName);
        const index = state.counties.findIndex(
          (county) =>
            normalizeCountyRouteValue(county.name) === requested ||
            (county.slug && normalizeCountyRouteValue(county.slug) === requested),
        );
        if (index >= 0) state.counties[index] = directCounty;
      }
      return directCounty;
    }
  } catch (error) {
    console.warn('[Geographic Data] Direct county lookup failed, using state directory fallback', error);
  }

  const state = await getStateByCodeAsync(stateCode);
  const requested = normalizeCountyRouteValue(countyName);
  // Check by name or slug, accepting the legacy "-county" route suffix.
  return state?.counties.find(
    (c) =>
      normalizeCountyRouteValue(c.name) === requested || (c.slug && normalizeCountyRouteValue(c.slug) === requested),
  );
}

export function getCitiesByCounty(stateCode: string, countyName: string): City[] {
  const county = getCounty(stateCode, countyName);
  return county?.cities || [];
}

export function getCityBySlug(stateCode: string, countyName: string, citySlug: string): City | undefined {
  const cities = getCitiesByCounty(stateCode, countyName);
  return cities.find((c) => c.slug.toLowerCase() === citySlug.toLowerCase());
}

export function getCity(stateCode: string, countyName: string, citySlug: string): City | undefined {
  return getCityBySlug(stateCode, countyName, citySlug);
}

export async function getCityAsync(stateCode: string, countyName: string, citySlug: string): Promise<City | undefined> {
  // A town route can resolve directly without waiting for the global state
  // index. This keeps deep links usable while the shallow index is loading.
  try {
    const { loadCityDirect } = await loadFirestoreService();
    const directCity = await loadCityDirect(stateCode.toUpperCase(), countyName.toLowerCase(), citySlug.toLowerCase());
    if (directCity) return directCity;
  } catch (error) {
    console.warn('[Geographic Data] Direct city lookup failed, using cache fallback', error);
  }

  const data = await getGeographicData();
  const state = data.find((candidate) => candidate.code.toLowerCase() === stateCode.toLowerCase());
  if (!state) return undefined;

  const requestedCounty = normalizeCountyRouteValue(countyName);
  const county = state.counties.find(
    (c) =>
      normalizeCountyRouteValue(c.name) === requestedCounty ||
      (c.slug && normalizeCountyRouteValue(c.slug) === requestedCounty),
  );

  // Try cache first
  if (county) {
    const cachedCity = county.cities.find((c) => c.slug.toLowerCase() === citySlug.toLowerCase());
    if (cachedCity) return cachedCity;
  }

  // Load the requested city directly instead of waiting for an entire state
  // deep-load. Town routes should not be blocked by thousands of sibling cities.
  const { loadCityDirect } = await loadFirestoreService();
  const countySlug = county?.slug || countyName;
  const cityFromFirestore = await loadCityDirect(stateCode, countySlug.toLowerCase(), citySlug.toLowerCase());
  return cityFromFirestore || undefined;
}

export function searchByZip(zipCode: string): City | undefined {
  if (!geographicDataCache) {
    return undefined;
  }

  const normalizedZip = normalizeZipCode(zipCode);
  if (!normalizedZip) return undefined;

  for (const state of geographicDataCache) {
    for (const county of state.counties) {
      for (const city of county.cities) {
        if (city.zipCodes.includes(normalizedZip)) {
          return city;
        }
      }
    }
  }
  return undefined;
}

export async function searchByZipAsync(zipCode: string): Promise<City | undefined> {
  const normalizedZip = normalizeZipCode(zipCode);
  if (!normalizedZip) return undefined;

  // Try Firestore direct search first (faster and works with shallow cache)
  const { searchCityByZip } = await loadFirestoreService();
  const city = await searchCityByZip(normalizedZip);
  if (city) return city;

  // Fallback to cache search
  const data = await getGeographicData();

  for (const state of data) {
    for (const county of state.counties) {
      for (const city of county.cities) {
        if (city.zipCodes.includes(normalizedZip)) {
          return city;
        }
      }
    }
  }
  return undefined;
}
